import React, { useMemo } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { ArchivedDocumentWithDetails, FileType, Order, VisibleColumnSpec } from "@/types";
import { useTranslation } from "react-i18next";
import NonSortableHeader from "@/components/molecules/NonSortableHeader";
import DataTable from "@/components/molecules/DataTable";
import { useVisibleColumns } from "@/hooks/useVisibleColumns";
import { useGetDocumentsFromString, useGetTransactionsDocIds } from "@/services/Documents";
import { useGetDeviationInvoices, useGetOrderTransactionsInvoices } from "@/services/Accounting";
import { useGetOrderAttachments } from "@/services/Orders";

interface OrderDocumentsTableProps {
  visibleColumns?: VisibleColumnSpec<ArchivedDocumentWithDetails>[];
  initialSort?: SortingState;
  pagination?: boolean;
  actionable?: boolean;
  initialRowsPerPage?: number;
  order: Order;
}
const OrderDocumentsTable: React.FC<OrderDocumentsTableProps> = ({
  visibleColumns,
  initialSort = null,
  pagination: _pagination = true,
  actionable: _actionable = true,
  initialRowsPerPage = 15,
  order,
}) => {
  const { t } = useTranslation();

  const invoices = useGetOrderTransactionsInvoices(order.transactions || [], {
    enabled: !!order.transactions,
  });

  const otherDocumentsIds = useGetTransactionsDocIds(order.transactions || [], order.company.id, {
    enabled: !!order.transactions,
  });

  const { data: deviationInvoices } = useGetDeviationInvoices(order.id);

  const { data: orderAttachmentsIds } = useGetOrderAttachments(order.id);

  const otherDocuments = useGetDocumentsFromString(
    [...(otherDocumentsIds || []), ...(orderAttachmentsIds || [])],
    order.company.id,
    (otherDocumentsIds?.length ?? 0) > 0 || (orderAttachmentsIds?.length ?? 0) > 0
  );

  const invoicesTranslated: ArchivedDocumentWithDetails[] = (invoices || []).map((inv) => ({
    id: inv.invoiceId.toString(),
    document: inv.document,
    companyIds: [],
    fileName: inv.documentTitle,
    companyLanguageId: 0,
    fileType: inv.fileType as FileType,
    createdTime: inv.created,
    mediaType: inv.mediaType,
    documentType: inv.documentType,
    documentTitle: inv.documentTitle,
    deleted: false,
  }));

  const deviationInvoicesTranslated: ArchivedDocumentWithDetails[] = (deviationInvoices || []).map((inv, index) => ({
    id: index.toString(),
    document: inv,
    companyIds: [],
    fileName: `DeviationInvoice_${index + 1}`,
    companyLanguageId: 0,
    fileType: "PDF" as FileType,
    createdTime: "0000-00-00T00:00:00Z",
    mediaType: "application/pdf",
    documentType: "INVOICE_DEVIATION",
    documentTitle: `DeviationInvoice_${index + 1}`,
    deleted: false,
  }));

  const otherDocs = otherDocuments?.files?.filter((doc) => doc !== undefined && doc !== null) ?? [];
  const otherImages = otherDocuments?.images?.filter((doc) => doc !== undefined && doc !== null) ?? [];

  const documents = useMemo(() => {
    const allDocs = [...invoicesTranslated, ...otherDocs, ...deviationInvoicesTranslated, ...otherImages];
    return allDocs.filter((doc, i, arr) => arr.findIndex((d) => d.id === doc.id) === i);
  }, [invoicesTranslated, otherDocs, deviationInvoicesTranslated, otherImages]);

  const baseColumns: ColumnDef<ArchivedDocumentWithDetails>[] = useMemo(
    () => [
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("dashboard:documentType")} />,
        id: "documentType",
        cell: ({ row }) => {
          const doc = row.original;
          return (
            <a
              download={t("enumerables:documentTypes." + doc.documentType)}
              href={`data:${doc.mediaType};base64,${doc.document}`}
            >
              {t("enumerables:documentTypes." + doc.documentType)}
            </a>
          );
        },
      },
      {
        header: ({ column }) => NonSortableHeader({ column, label: t("common:name") }),
        id: "documentTitle",
        cell: ({ row }) => row.original.documentTitle,
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("dashboard:fileType")} />,
        id: "fileType",
        cell: ({ row }) => row.original.fileType,
      },
    ],
    [t, documents]
  );

  const columns = useVisibleColumns<ArchivedDocumentWithDetails>(baseColumns, visibleColumns);

  return (
    <DataTable
      onRowClick={undefined}
      data={documents || []}
      columns={columns}
      isLoading={false}
      mobileHeaderCell={0}
      showPagination={false}
      initialSort={initialSort ?? undefined}
      initialRowsPerPage={initialRowsPerPage}
    />
  );
};

export default OrderDocumentsTable;
